import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  adjacentProductSearchQuerySchema,
  adjacentProductSearchResponseSchema,
  savedSearchCreateBodySchema,
  savedSearchListSchema,
  savedSearchRenameBodySchema,
  savedSearchRunSchema,
  savedSearchSchema,
  searchFullResultsPageQuerySchema,
  searchFullResultsPageSchema,
  searchJumpToSchema,
  searchQuerySchema,
  searchResultsSchema,
  type AdjacentProductSearchResponse,
  type FirstPartyIntegrationKey,
  type SavedSearch,
  type SavedSearchCreateBody,
  type SavedSearchList,
  type SavedSearchRenameBody,
  type SavedSearchRun,
  type SearchFullResultsPage,
  type SearchJumpTo,
  type SearchResults,
} from '@molt/shared';
import { del, emptyResponseSchema, get, patch, post } from './api';
import { queryKeys } from './query-keys';

export function getGlobalSearch(q: string): Promise<SearchResults> {
  const query = searchQuerySchema.parse({ q });
  const params = new URLSearchParams({ q: query.q });
  return get(`/search?${params.toString()}`, searchResultsSchema);
}

export function globalSearchQueryOptions(q: string) {
  const query = searchQuerySchema.parse({ q });
  return queryOptions({
    queryKey: queryKeys.search.typeahead(query.q),
    queryFn: () => getGlobalSearch(query.q),
  });
}

export function getSearchJumpTo(): Promise<SearchJumpTo> {
  return get('/search/jump-to', searchJumpToSchema);
}

export function searchJumpToQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.search.jumpTo(),
    queryFn: getSearchJumpTo,
  });
}

export interface FullSearchResultsRequest {
  readonly q: string;
  readonly includeArchived?: boolean;
  readonly includeClosedBoards?: boolean;
  /**
   * `FR-SEARCH-017`'s *"toggle for whether card descriptions are matched"*.
   *
   * ⚠ **Omitted means `true`, not `false`.** `FR-SEARCH-021` matches title,
   * description, comment bodies and checklist text unconditionally, so
   * descriptions are searched unless a caller asks otherwise; the wire
   * default on `searchFullResultsPageQuerySchema` says the same thing.
   */
  readonly matchDescriptions?: boolean;
}

export interface AdjacentProductSearchRequest {
  readonly q: string;
  readonly provider?: FirstPartyIntegrationKey;
}

/**
 * ⚠ **Parses against the PAGE query schema, not `searchFullResultsQuerySchema`.**
 * The base schema is a `strictObject`, so a fourth key there is a parse
 * *throw* at runtime rather than a silent drop — the toggle would take the
 * page down instead of doing nothing. `searchFullResultsPageQuerySchema` is
 * `.extend`ed from it (still strict, so a mistyped key is still refused) and
 * is the exact schema `GET /search/results` validates its querystring with,
 * which is what keeps this client and that route reading one contract.
 */
function parseFullSearchResultsRequest(query: FullSearchResultsRequest) {
  return searchFullResultsPageQuerySchema.parse({
    q: query.q,
    ...(query.includeArchived === undefined
      ? {}
      : { includeArchived: query.includeArchived ? 'true' : 'false' }),
    ...(query.includeClosedBoards === undefined
      ? {}
      : { includeClosedBoards: query.includeClosedBoards ? 'true' : 'false' }),
    ...(query.matchDescriptions === undefined
      ? {}
      : { matchDescriptions: query.matchDescriptions ? 'true' : 'false' }),
  });
}

/**
 * ⚠ **Parses the response with `searchFullResultsPageSchema`, the wider row.**
 * `searchFullResultsSchema` is a `z.object`, so it STRIPS `dueAt` and
 * `completed` at this boundary — every result row arrived with them and left
 * without them, which is why `FR-SEARCH-017`'s edge case (*"cards whose due
 * date is marked complete are visually distinguished in the result list"*)
 * had a renderer and no data to render. The narrow schema stays in this file
 * for `GET /saved-searches/:id/results`, whose rows really are narrow.
 *
 * ⚠ **`matchDescriptions` is emitted only when FALSE.** The wire default is
 * `true`, so emitting `matchDescriptions=true` would add a byte to every URL
 * this client has ever sent while meaning exactly what the absent parameter
 * already means. The inversion keeps every shipped request identical and
 * makes the parameter's presence proof that a member turned the toggle off.
 */
export function getFullSearchResults(
  query: FullSearchResultsRequest,
): Promise<SearchFullResultsPage> {
  const parsed = parseFullSearchResultsRequest(query);
  const params = new URLSearchParams({ q: parsed.q });
  if (parsed.includeArchived) params.set('includeArchived', 'true');
  if (parsed.includeClosedBoards) params.set('includeClosedBoards', 'true');
  if (!parsed.matchDescriptions) params.set('matchDescriptions', 'false');
  return get(`/search/results?${params.toString()}`, searchFullResultsPageSchema);
}

export function fullSearchResultsQueryOptions(query: FullSearchResultsRequest) {
  const parsed = parseFullSearchResultsRequest(query);
  return queryOptions({
    queryKey: queryKeys.search.fullResults(
      parsed.q,
      parsed.includeArchived,
      parsed.includeClosedBoards,
      parsed.matchDescriptions,
    ),
    queryFn: () => getFullSearchResults(parsed),
  });
}

function parseAdjacentProductSearchRequest(query: AdjacentProductSearchRequest) {
  return adjacentProductSearchQuerySchema.parse({
    q: query.q,
    ...(query.provider === undefined ? {} : { provider: query.provider }),
  });
}

export function getAdjacentProductSearch(
  query: AdjacentProductSearchRequest,
): Promise<AdjacentProductSearchResponse> {
  const parsed = parseAdjacentProductSearchRequest(query);
  const params = new URLSearchParams({ q: parsed.q });
  if (parsed.provider !== undefined) params.set('provider', parsed.provider);
  return get(
    `/search/adjacent-products?${params.toString()}`,
    adjacentProductSearchResponseSchema,
  );
}

export function adjacentProductSearchQueryOptions(query: AdjacentProductSearchRequest) {
  const parsed = parseAdjacentProductSearchRequest(query);
  const request =
    parsed.provider === undefined
      ? { q: parsed.q }
      : { q: parsed.q, provider: parsed.provider };
  return queryOptions({
    queryKey: queryKeys.search.adjacentProducts(parsed.q, parsed.provider),
    queryFn: () => getAdjacentProductSearch(request),
  });
}

export interface RenameSavedSearchVariables {
  readonly id: string;
  readonly name: string;
}

export function getSavedSearches(): Promise<SavedSearchList> {
  return get('/saved-searches', savedSearchListSchema);
}

export function createSavedSearch(body: SavedSearchCreateBody): Promise<SavedSearch> {
  return post(
    '/saved-searches',
    savedSearchSchema,
    savedSearchCreateBodySchema.parse(body),
  );
}

export function renameSavedSearch(
  id: string,
  body: SavedSearchRenameBody,
): Promise<SavedSearch> {
  return patch(
    `/saved-searches/${encodeURIComponent(id)}/name`,
    savedSearchSchema,
    savedSearchRenameBodySchema.parse(body),
  );
}

export function deleteSavedSearch(id: string): Promise<null> {
  return del(`/saved-searches/${encodeURIComponent(id)}`, emptyResponseSchema);
}

export function runSavedSearch(id: string): Promise<SavedSearchRun> {
  return get(`/saved-searches/${encodeURIComponent(id)}/results`, savedSearchRunSchema);
}

export function savedSearchesQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.savedSearches.list(),
    queryFn: getSavedSearches,
  });
}

export function savedSearchRunQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.savedSearches.results(id),
    queryFn: () => runSavedSearch(id),
  });
}

function writeSavedSearch(
  previous: SavedSearchList | undefined,
  savedSearch: SavedSearch,
): SavedSearchList {
  const rows = previous?.savedSearches ?? [];
  const existingIndex = rows.findIndex((row) => row.id === savedSearch.id);
  if (existingIndex === -1) {
    return { savedSearches: [savedSearch, ...rows] };
  }
  return {
    savedSearches: rows.map((row) => (row.id === savedSearch.id ? savedSearch : row)),
  };
}

export function useCreateSavedSearch(): UseMutationResult<
  SavedSearch,
  Error,
  SavedSearchCreateBody
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSavedSearch,
    onSuccess: (savedSearch) => {
      queryClient.setQueryData<SavedSearchList>(
        queryKeys.savedSearches.list(),
        (previous) => writeSavedSearch(previous, savedSearch),
      );
    },
  });
}

export function useRenameSavedSearch(): UseMutationResult<
  SavedSearch,
  Error,
  RenameSavedSearchVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }) => renameSavedSearch(id, { name }),
    onSuccess: (savedSearch) => {
      queryClient.setQueryData<SavedSearchList>(
        queryKeys.savedSearches.list(),
        (previous) => writeSavedSearch(previous, savedSearch),
      );
    },
  });
}

export function useDeleteSavedSearch(): UseMutationResult<null, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedSearch,
    onSuccess: (_result, id) => {
      queryClient.setQueryData<SavedSearchList>(
        queryKeys.savedSearches.list(),
        (previous) => ({
          savedSearches: (previous?.savedSearches ?? []).filter((row) => row.id !== id),
        }),
      );
      queryClient.removeQueries({ queryKey: queryKeys.savedSearches.results(id) });
    },
  });
}

export function useRunSavedSearch(): UseMutationResult<SavedSearchRun, Error, string> {
  return useMutation({ mutationFn: runSavedSearch });
}
